export const PROJECT_TRACKING_PROFILE = {
  standard: 'standard',
  operationalReporting: 'operational-reporting',
  operationalSiteReporting: 'operational-site-reporting',
  pemdReadiness: 'pemd-readiness',
} as const

export const PROJECT_TRACKING_PROFILE_PREFERENCE_KEY =
  'project-tracking-profile'

export type ProjectTrackingProfile =
  (typeof PROJECT_TRACKING_PROFILE)[keyof typeof PROJECT_TRACKING_PROFILE]

export type ProjectTrackingProfilePreset = 'default' | 'reporting' | 'pemd'

export type ProjectTrackingProfileContract = {
  label: string
  description: string
  overviewSummary: string
  presets: {
    inventory: ProjectTrackingProfilePreset
    removal: ProjectTrackingProfilePreset
  }
  blocks: {
    overviewOperationalPreview: boolean
    statisticsOperationalBalance: boolean
    statisticsLegacyCondition: boolean
    statisticsLegacyDataQuality: boolean
    statisticsIndicatorSection: boolean
    removalCompletionBanner: boolean
  }
  completeness: {
    estimatedReporting: 'standard' | 'expected'
    projectSiteTime: 'none' | 'optional' | 'expected'
    outputsQualification: 'none' | 'optional' | 'expected'
  }
  pilotage: {
    showOperationalReporting: boolean
    showOperationalSiteReporting: boolean
  }
}

export type ProjectTrackingProjectLike = {
  trackingProfile?: unknown
  preferences?: unknown
}

export type ProjectTrackingFeatures = {
  profile: ProjectTrackingProfile
  label: string
  description: string
  overviewSummary: string
  contract: ProjectTrackingProfileContract
  showOperationalReporting: boolean
  showOperationalSiteReporting: boolean
  useReportingResourcePreset: boolean
  useReportingRemovalPreset: boolean
}

export const DEFAULT_PROJECT_TRACKING_PROFILE: ProjectTrackingProfile =
  PROJECT_TRACKING_PROFILE.standard

export const PROJECT_TRACKING_PROFILE_OPTIONS: Array<{
  label: string
  value: ProjectTrackingProfile
}> = [
  {
    label: 'Standard',
    value: PROJECT_TRACKING_PROFILE.standard,
  },
  {
    label: 'Reporting opérationnel',
    value: PROJECT_TRACKING_PROFILE.operationalReporting,
  },
  {
    label: 'Reporting opérationnel chantier',
    value: PROJECT_TRACKING_PROFILE.operationalSiteReporting,
  },
  {
    label: 'PEMD',
    value: PROJECT_TRACKING_PROFILE.pemdReadiness,
  },
]

const PROJECT_TRACKING_PROFILE_LABELS = Object.freeze(
  PROJECT_TRACKING_PROFILE_OPTIONS.reduce<
    Record<ProjectTrackingProfile, string>
  >(
    (acc, option) => {
      acc[option.value] = option.label
      return acc
    },
    {} as Record<ProjectTrackingProfile, string>,
  ),
)

const PROJECT_TRACKING_PROFILE_CONTRACTS = Object.freeze({
  [PROJECT_TRACKING_PROFILE.standard]: {
    label: 'Standard',
    description:
      'Pilotage projet standard avec surfaces legacy et sans suivi opérationnel chantier dédié.',
    overviewSummary: 'Pilotage projet standard sans lecture reporting dédiée.',
    presets: {
      inventory: 'default',
      removal: 'default',
    },
    blocks: {
      overviewOperationalPreview: false,
      statisticsOperationalBalance: false,
      statisticsLegacyCondition: true,
      statisticsLegacyDataQuality: true,
      statisticsIndicatorSection: true,
      removalCompletionBanner: false,
    },
    completeness: {
      estimatedReporting: 'standard',
      projectSiteTime: 'none',
      outputsQualification: 'none',
    },
    pilotage: {
      showOperationalReporting: false,
      showOperationalSiteReporting: false,
    },
  },
  [PROJECT_TRACKING_PROFILE.operationalReporting]: {
    label: 'Reporting opérationnel',
    description:
      'Active le référentiel diagnostic et les indicateurs opérationnels sans imposer le suivi chantier complet des sorties.',
    overviewSummary:
      'Lecture diagnostic avec objectif et premiers agrégats opérationnels.',
    presets: {
      inventory: 'reporting',
      removal: 'default',
    },
    blocks: {
      overviewOperationalPreview: true,
      statisticsOperationalBalance: true,
      statisticsLegacyCondition: false,
      statisticsLegacyDataQuality: false,
      statisticsIndicatorSection: true,
      removalCompletionBanner: false,
    },
    completeness: {
      estimatedReporting: 'expected',
      projectSiteTime: 'optional',
      outputsQualification: 'optional',
    },
    pilotage: {
      showOperationalReporting: true,
      showOperationalSiteReporting: false,
    },
  },
  [PROJECT_TRACKING_PROFILE.operationalSiteReporting]: {
    label: 'Reporting opérationnel chantier',
    description:
      'Ajoute au diagnostic le suivi chantier du temps projet et la qualification opérationnelle des sorties.',
    overviewSummary:
      "Ajoute le temps chantier et permet de suivre l'avancement des sorties et ce qu'il reste à compléter.",
    presets: {
      inventory: 'reporting',
      removal: 'reporting',
    },
    blocks: {
      overviewOperationalPreview: true,
      statisticsOperationalBalance: true,
      statisticsLegacyCondition: false,
      statisticsLegacyDataQuality: false,
      statisticsIndicatorSection: true,
      removalCompletionBanner: true,
    },
    completeness: {
      estimatedReporting: 'expected',
      projectSiteTime: 'expected',
      outputsQualification: 'expected',
    },
    pilotage: {
      showOperationalReporting: true,
      showOperationalSiteReporting: true,
    },
  },
  [PROJECT_TRACKING_PROFILE.pemdReadiness]: {
    label: 'PEMD',
    description:
      'Active les vues PEMD pour classifier, quantifier et contrôler les champs nécessaires au diagnostic et à la validation réglementaire.',
    overviewSummary:
      'PEMD centré sur les classifications, quantités, matériaux et codes déchets candidats.',
    presets: {
      inventory: 'pemd',
      removal: 'pemd',
    },
    blocks: {
      overviewOperationalPreview: false,
      statisticsOperationalBalance: false,
      statisticsLegacyCondition: true,
      statisticsLegacyDataQuality: true,
      statisticsIndicatorSection: true,
      removalCompletionBanner: false,
    },
    completeness: {
      estimatedReporting: 'expected',
      projectSiteTime: 'optional',
      outputsQualification: 'optional',
    },
    pilotage: {
      showOperationalReporting: false,
      showOperationalSiteReporting: false,
    },
  },
} satisfies Record<ProjectTrackingProfile, ProjectTrackingProfileContract>)

const PROJECT_TRACKING_PROFILE_PREFERENCE_PREFIX = `${PROJECT_TRACKING_PROFILE_PREFERENCE_KEY}:`

function normalizeProjectTrackingProfileString(
  value: unknown,
): ProjectTrackingProfile | null {
  if (typeof value !== 'string') return null

  return PROJECT_TRACKING_PROFILE_OPTIONS.find(
    (option) => option.value === value,
  )?.value
    ? (value as ProjectTrackingProfile)
    : null
}

function decodeProjectTrackingProfilePreference(
  value: unknown,
): ProjectTrackingProfile | null {
  if (typeof value !== 'string') return null

  const normalizedValue = value.startsWith(
    PROJECT_TRACKING_PROFILE_PREFERENCE_PREFIX,
  )
    ? value.slice(PROJECT_TRACKING_PROFILE_PREFERENCE_PREFIX.length)
    : value

  return normalizeProjectTrackingProfileString(normalizedValue)
}

export function readProjectTrackingProfilePreference(
  preferences: unknown,
): ProjectTrackingProfile | null {
  if (!Array.isArray(preferences)) return null

  for (const preference of preferences) {
    const decodedProfile = decodeProjectTrackingProfilePreference(preference)
    if (decodedProfile) return decodedProfile
  }

  return null
}

export function buildProjectTrackingPreferences({
  existingPreferences,
  profile,
}: {
  existingPreferences: unknown
  profile: ProjectTrackingProfile
}) {
  const preferences = Array.isArray(existingPreferences)
    ? existingPreferences.filter(
        (preference): preference is string => typeof preference === 'string',
      )
    : []

  const filteredPreferences = preferences.filter(
    (preference) => !decodeProjectTrackingProfilePreference(preference),
  )

  return [
    ...filteredPreferences,
    `${PROJECT_TRACKING_PROFILE_PREFERENCE_PREFIX}${profile}`,
  ]
}

export function normalizeProjectTrackingProfile(
  value: unknown,
): ProjectTrackingProfile | null {
  return (
    normalizeProjectTrackingProfileString(value) ??
    readProjectTrackingProfilePreference(value)
  )
}

export function readProjectTrackingProfileValue(
  project: ProjectTrackingProjectLike | null | undefined,
) {
  return project?.preferences ?? project?.trackingProfile ?? null
}

export function resolveProjectTrackingProfile({
  value,
}: {
  value: unknown
  hasOperationalReportingData?: boolean
}): ProjectTrackingProfile {
  const normalizedProfile = normalizeProjectTrackingProfile(value)
  if (normalizedProfile) return normalizedProfile

  return DEFAULT_PROJECT_TRACKING_PROFILE
}

export function resolveProjectTrackingProfileFromProject({
  project,
  hasOperationalReportingData = false,
}: {
  project: ProjectTrackingProjectLike | null | undefined
  hasOperationalReportingData?: boolean
}) {
  return resolveProjectTrackingProfile({
    value: readProjectTrackingProfileValue(project),
    hasOperationalReportingData,
  })
}

export function getProjectTrackingProfileLabel(
  value: ProjectTrackingProfile | null | undefined,
) {
  const normalizedProfile = value
    ? normalizeProjectTrackingProfile(value)
    : DEFAULT_PROJECT_TRACKING_PROFILE

  return PROJECT_TRACKING_PROFILE_LABELS[
    normalizedProfile ?? DEFAULT_PROJECT_TRACKING_PROFILE
  ]
}

export function getProjectTrackingProfileContract(
  value: ProjectTrackingProfile | null | undefined,
) {
  const normalizedProfile = value
    ? normalizeProjectTrackingProfile(value)
    : DEFAULT_PROJECT_TRACKING_PROFILE

  return PROJECT_TRACKING_PROFILE_CONTRACTS[
    normalizedProfile ?? DEFAULT_PROJECT_TRACKING_PROFILE
  ]
}

export function getProjectTrackingProfileOption({
  value,
  hasOperationalReportingData = false,
}: {
  value: unknown
  hasOperationalReportingData?: boolean
}) {
  const resolvedProfile = resolveProjectTrackingProfile({
    value,
    hasOperationalReportingData,
  })

  return (
    PROJECT_TRACKING_PROFILE_OPTIONS.find(
      (option) => option.value === resolvedProfile,
    ) ?? PROJECT_TRACKING_PROFILE_OPTIONS[0]
  )
}

export function getProjectTrackingProfileOptionFromProject({
  project,
  hasOperationalReportingData = false,
}: {
  project: ProjectTrackingProjectLike | null | undefined
  hasOperationalReportingData?: boolean
}) {
  return getProjectTrackingProfileOption({
    value: readProjectTrackingProfileValue(project),
    hasOperationalReportingData,
  })
}

export function isOperationalProjectTrackingProfile(
  value: ProjectTrackingProfile | null | undefined,
) {
  return (
    value === PROJECT_TRACKING_PROFILE.operationalReporting ||
    value === PROJECT_TRACKING_PROFILE.operationalSiteReporting
  )
}

export function isOperationalSiteProjectTrackingProfile(
  value: ProjectTrackingProfile | null | undefined,
) {
  return value === PROJECT_TRACKING_PROFILE.operationalSiteReporting
}

export function resolveProjectTrackingFeatures({
  value,
  hasOperationalReportingData = false,
}: {
  value: unknown
  hasOperationalReportingData?: boolean
}): ProjectTrackingFeatures {
  const profile = resolveProjectTrackingProfile({
    value,
    hasOperationalReportingData,
  })
  const contract = getProjectTrackingProfileContract(profile)

  return {
    profile,
    label: contract.label,
    description: contract.description,
    overviewSummary: contract.overviewSummary,
    contract,
    showOperationalReporting: contract.pilotage.showOperationalReporting,
    showOperationalSiteReporting:
      contract.pilotage.showOperationalSiteReporting,
    useReportingResourcePreset: contract.presets.inventory === 'reporting',
    useReportingRemovalPreset: contract.presets.removal === 'reporting',
  }
}

export function resolveProjectTrackingFeaturesFromProject({
  project,
  hasOperationalReportingData = false,
}: {
  project: ProjectTrackingProjectLike | null | undefined
  hasOperationalReportingData?: boolean
}) {
  return resolveProjectTrackingFeatures({
    value: readProjectTrackingProfileValue(project),
    hasOperationalReportingData,
  })
}

export function buildProjectTrackingProjectUpdate({
  project,
  profile,
}: {
  project: ProjectTrackingProjectLike | null | undefined
  profile: ProjectTrackingProfile
}) {
  return {
    preferences: buildProjectTrackingPreferences({
      existingPreferences: project?.preferences,
      profile,
    }),
  }
}
